import { StyledBodyText, StyledHeaderText } from '../../../../../common/Styleguide/Common/Text';
import React, { useState } from 'react';
import { getInputValue } from '../../../../../common/react';
import { isValidEmail } from '../../../../../common/validations';
import { DefaultInput } from '../../../../../common/Styleguide/Common/Input';
import Mail from '../../../../../common/icons/Mail';
import { DeveloperProfile } from '../profile-graphql';
import { StyledLoadingButton } from '../../../../../common/Styleguide/Common/Button';
import { FormControl, Checkbox, FormControlLabel } from '@mui/material';
import TextAlert from '../../UserDeveloper/TextAlert';
import { toast } from 'react-toastify';
import { updateEmailPreferences } from '../gql/email-preferences';
import { nonEmptyString } from '@crazygames/utils/isDefined';

export interface Props {
  profile: DeveloperProfile;
}

const EmailPreferences: React.FC<Props> = ({ profile }: Props) => {
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [gameFeedbackEmail, setGameFeedbackEmail] = useState<string>(profile.gameFeedbackEmail?.email || profile.email);
  const [gameFeedbackEmailError, setGameFeedbackEmailError] = useState<string | null>(null);
  const [iapSupportEmail, setIapSupportEmail] = useState<string>(profile.iapSupportEmail?.email || profile.email);
  const [iapSupportEmailError, setIapSupportEmailError] = useState<string | null>(null);
  const [receiveFeedback, setReceiveFeedback] = useState<boolean>(!profile.hasOptedOutUserFeedbackEmail);
  const [gameFeedbackEmailNeedsVerify, setGameFeedbackEmailNeedsVerify] = useState<boolean>(
    profile.gameFeedbackEmail?.isVerified === false,
  );
  const [iapSupportEmailNeedsVerify, setIapSupportEmailNeedsVerify] = useState<boolean>(profile.iapSupportEmail?.isVerified === false);

  const onSaveClick = async () => {
    setIsSaving(true);
    try {
      const response = await updateEmailPreferences({
        gameFeedbackEmail,
        iapSupportEmail,
        hasOptedOutUserFeedbackEmail: !receiveFeedback,
      });
      const data = response.data?.developerUpdateEmailPreferences;
      setGameFeedbackEmailNeedsVerify(data?.gameFeedbackEmail?.isVerified === false);
      setIapSupportEmailNeedsVerify(data?.iapSupportEmail?.isVerified === false);
    } catch (e) {
      console.error('Error saving email preferences', JSON.stringify(e, null, 2));
      toast.error('Error saving email preferences', { autoClose: false });
    } finally {
      setIsSaving(false);
    }
  };

  const canSave = () => {
    return !gameFeedbackEmailError && !iapSupportEmailError;
  };

  return (
    <>
      <StyledHeaderText variant="h2">Email preferences</StyledHeaderText>
      <DefaultInput
        value={gameFeedbackEmail}
        placeholder="Ex: contact@crazygames.com"
        adormentIcon={<Mail />}
        error={gameFeedbackEmailError !== null}
        errorText={`${gameFeedbackEmailError}`}
        onChange={(ev) => {
          setGameFeedbackEmail(getInputValue(ev));
          setGameFeedbackEmailError(null);
        }}
        onBlur={() => {
          if (!isValidEmail(gameFeedbackEmail) || !nonEmptyString(gameFeedbackEmail)) {
            setGameFeedbackEmailError('Invalid email');
          }
        }}
        header="Game feedback email"
        sx={{ width: '50%' }}
      />
      {gameFeedbackEmailNeedsVerify && (
        <TextAlert sx={{ mt: 1 }}>Email not verified. Please check your inbox to verify your email.</TextAlert>
      )}
      <FormControl>
        <FormControlLabel
          control={<Checkbox checked={receiveFeedback} onChange={(ev) => setReceiveFeedback(ev.target.checked)} />}
          label="I want to receive emails with user feedback"
        />
      </FormControl>
      <StyledBodyText variant="bodyLower" color={'white60'} sx={{ mt: 0 }}>
        Users can send feedback about your games via a form on the game page. Your email address won't be shared.
      </StyledBodyText>
      {!receiveFeedback && (
        <TextAlert sx={{ mb: 2 }}>
          Warning: You will miss important user feedback. Games that do not take action on relevant user feedback might be taken offline.
        </TextAlert>
      )}
      <DefaultInput
        value={iapSupportEmail}
        placeholder="Ex: contact@crazygames.com"
        adormentIcon={<Mail />}
        error={iapSupportEmailError !== null}
        errorText={`${iapSupportEmailError}`}
        onChange={(ev) => {
          setIapSupportEmail(getInputValue(ev));
          setIapSupportEmailError(null);
        }}
        onBlur={() => {
          if (!isValidEmail(iapSupportEmail) || !nonEmptyString(iapSupportEmail)) {
            setIapSupportEmailError('Invalid email');
          }
        }}
        sx={{ width: '50%' }}
        header="IGP support email"
      />
      <StyledBodyText variant="bodyLower" color={'white60'} sx={{ mt: 0, mb: 1 }}>
        In-game purchase support emails will be sent to this address.
      </StyledBodyText>
      {iapSupportEmailNeedsVerify && (
        <TextAlert sx={{ mb: 2 }}>Email not verified. Please check your inbox to verify your email.</TextAlert>
      )}
      <StyledLoadingButton loading={isSaving} onClick={onSaveClick} disabled={!canSave()}>
        Save changes
      </StyledLoadingButton>
    </>
  );
};

export default EmailPreferences;
